import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mockData';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => handleNext(), 5000);
    return () => clearInterval(interval);
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 400);
  };
  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const getVisible = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return [
      { ...testimonials[prev], pos: 'left' },
      { ...testimonials[currentIndex], pos: 'center' },
      { ...testimonials[next], pos: 'right' },
    ];
  };

  return (
    <section id="testimonials" className="py-24 bg-[#F4F4F5]" data-testid="testimonials-section">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Testimonials</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{fontFamily:'Outfit,sans-serif'}}>
            Real Results. Real Clients.
          </h2>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-5 py-4">
            {getVisible().map((t, i) => {
              const isCenter = t.pos === 'center';
              return (
                <Card
                  key={`${t.id}-${i}`}
                  className={`transition-all duration-500 ease-out ${
                    isCenter
                      ? 'scale-100 opacity-100 z-20 w-full max-w-xl border-[#002FA7] shadow-[0_8px_32px_rgba(0,47,167,0.1)]'
                      : 'scale-90 opacity-40 z-10 w-full max-w-sm hidden lg:block border-zinc-200'
                  } bg-white rounded-lg`}
                  data-testid={isCenter ? 'testimonial-active' : undefined}
                >
                  <CardContent className="pt-8 pb-8 px-8">
                    <Badge className="bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20 text-xs mb-5">{t.service}</Badge>
                    <p className="text-zinc-600 text-sm leading-relaxed mb-6 min-h-[80px]">"{t.content}"</p>
                    <div className="flex items-center gap-1 mb-5">
                      {[...Array(t.rating)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-[#002FA7] text-[#002FA7]" />
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-[#002FA7] text-white flex items-center justify-center font-bold text-sm" style={{fontFamily:'Outfit,sans-serif'}}>{t.name.charAt(0)}</div>
                      <div>
                        <div className="font-semibold text-[#09090B] text-sm">{t.name}</div>
                        <div className="text-xs text-zinc-500">{t.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button onClick={handlePrev} disabled={isAnimating} className="bg-[#002FA7] hover:bg-[#001D66] text-white rounded-md w-10 h-10 p-0" data-testid="testimonial-prev">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-1.5">
              {testimonials.map((_, index) => (
                <button key={index}
                  onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 400); }}}
                  className={`transition-all duration-300 rounded-full ${index === currentIndex ? 'w-6 h-1.5 bg-[#002FA7]' : 'w-1.5 h-1.5 bg-zinc-300 hover:bg-[#002FA7]/50'}`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>
            <Button onClick={handleNext} disabled={isAnimating} className="bg-[#002FA7] hover:bg-[#001D66] text-white rounded-md w-10 h-10 p-0" data-testid="testimonial-next">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
